import { useEffect, useState } from 'react';
import { Badge } from '@auctisync/ui';
import { urgencyLabel, urgencyTierFromIso, type UrgencyTier } from '../lib/urgency';

interface UrgencyBadgeProps {
  endsAt: string | null;
  nowServerMs: () => number;
  /**
   * Show the textual countdown alongside the urgency label (e.g.
   * "Final minutes · 3m 42s"). Default true for the lot detail page; pass
   * false on dense list views.
   */
  showCountdown?: boolean;
  className?: string;
}

/**
 * Module #23 chunk 1 — visual urgency badge that re-tiers itself as the
 * countdown ticks. Picks one of urgency_low/medium/high/critical Badge
 * variants based on time remaining. Pulses red in the final 5 minutes.
 *
 * Hidden entirely when ends_at is null or the lot has ended (callers can
 * branch on the absence rather than showing a stale "ended" pill — the
 * parent page typically renders its own "Sold" / "Closed" state).
 */
export function UrgencyBadge({
  endsAt,
  nowServerMs,
  showCountdown = true,
  className,
}: UrgencyBadgeProps) {
  const [tier, setTier] = useState<UrgencyTier | null>(() =>
    urgencyTierFromIso(endsAt, nowServerMs()),
  );
  const [countdownLabel, setCountdownLabel] = useState(() =>
    computeCountdown(endsAt, nowServerMs()),
  );

  useEffect(() => {
    if (!endsAt) return;
    const tick = () => {
      const now = nowServerMs();
      setTier(urgencyTierFromIso(endsAt, now));
      setCountdownLabel(computeCountdown(endsAt, now));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt, nowServerMs]);

  if (!tier || tier === 'ended') return null;

  const variantKey = `urgency_${tier}` as const;

  return (
    <Badge variant={variantKey} className={className}>
      <span>{urgencyLabel(tier)}</span>
      {showCountdown && countdownLabel ? <span>· {countdownLabel}</span> : null}
    </Badge>
  );
}

function computeCountdown(endsAt: string | null, nowMs: number): string {
  if (!endsAt) return '';
  const endMs = new Date(endsAt).getTime();
  if (Number.isNaN(endMs)) return '';
  const diffMs = endMs - nowMs;
  if (diffMs <= 0) return '';
  const seconds = Math.floor(diffMs / 1000);
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
